import React from "react";

import { Box, Divider, FormControl, FormLabel, Grid, GridItem, Text } from "@chakra-ui/react";

import { CheckboxGroup } from "common/components/CheckboxGroup/CheckboxGroup";
import InputField from "common/components/InputField/InputField";
import TextAreaInput from "common/components/TextAreaInput/TextAreaInput";
import { formatNumber } from "helpers/formatNumber";
import { useAppSelector } from "store/store";

import { ISeccionProps } from "./types";
import { useAutoInputSend } from "./useAutoInputSend";
import { useInputDiffDetector } from "./useInputDiffDetector";
import { useIsDisabled } from "./useIsDisabled";
import { SeccionHeader } from "../SeccionHeader/SeccionHeader";
import SeccionSwitch from "../SeccionSwitch/SeccionSwitch";

const Seccion06: React.FC<ISeccionProps> = (props) => {
	const evaluacion = useAppSelector((state) => state.evaluaciones.evaluacionSeleccionada.data);
	const evaluacionAnterior = useAppSelector((state) => state.evaluaciones.evaluacionSeleccionadaAnterior.data);
	const seccionDataActual = useAppSelector((state) => state.evaluaciones.evaluacionSeleccionada.data?.seccion_6);
	const printView = useAppSelector((state) => state.usuario.printView);

	const { sendInputValue } = useAutoInputSend(
		evaluacion?.cod_eb.toString() || "",
		evaluacion?.numero_evaluacion.toString() || "",
	);

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const changeDetector = useInputDiffDetector(evaluacionAnterior, evaluacion!, "seccion_6");

	const isDisabled = useIsDisabled(evaluacion) || seccionDataActual?.seccion_6_completada === 1;
	const selectorDisabled = useIsDisabled(evaluacion);

	const handleNextSectionNavigation = () => props.setSeccionActiva && props.setSeccionActiva(7);

	const totalKgMediosPropios = React.useMemo(() => {
		if (!seccionDataActual || !(seccionDataActual?.factor_euros_kilos > 0)) return 0;
		return (
			seccionDataActual.kilos_ano_medios_propios +
			seccionDataActual.euros_ano_medios_propios / seccionDataActual.factor_euros_kilos
		);
	}, [seccionDataActual]);

	const totalKgOtrasAportaciones = React.useMemo(() => {
		if (!seccionDataActual || !(seccionDataActual?.factor_euros_kilos > 0)) return 0;
		return (
			seccionDataActual.kilos_ano_otras_aportaciones +
			seccionDataActual.euros_ano_otras_aportaciones / seccionDataActual.factor_euros_kilos
		);
	}, [seccionDataActual]);

	const totalKgOtrosMedios = React.useMemo(() => {
		return totalKgMediosPropios + totalKgOtrasAportaciones;
	}, [totalKgMediosPropios, totalKgOtrasAportaciones]);

	React.useEffect(() => {
		if (totalKgOtrosMedios !== seccionDataActual?.total_ano_otras_aportaciones) {
			sendInputValue("total_ano_otras_aportaciones", totalKgOtrosMedios);
		}
	}, [totalKgOtrosMedios]);

	return (
		<Box>
			<SeccionHeader seccion="06" factor_euros_kilos={seccionDataActual?.factor_euros_kilos.toString() ?? " "} />
			<Box width="100%">
				<Divider marginBottom="1.5rem" color="gray.200" />
				<Text textStyle="body2" color="black" fontWeight="700">
					Medios propios
				</Text>
				<Grid
					templateColumns="repeat(3, 1fr)"
					gap="2rem"
					alignItems="flex-end"
					mt="1rem"
					style={{ breakInside: "avoid" }}
				>
					<GridItem>
						<FormControl>
							<FormLabel>kg en 12 meses</FormLabel>
							<InputField
								name="kilos_ano_medios_propios"
								defaultValue={formatNumber(seccionDataActual?.kilos_ano_medios_propios)}
								{...changeDetector("kilos_ano_medios_propios")}
								isDisabled={isDisabled}
							/>
						</FormControl>
					</GridItem>
					<GridItem>
						<FormControl>
							<FormLabel>€ en 12 meses</FormLabel>
							<InputField
								name="euros_ano_medios_propios"
								defaultValue={formatNumber(seccionDataActual?.euros_ano_medios_propios)}
								{...changeDetector("euros_ano_medios_propios")}
								isDisabled={isDisabled}
							/>
						</FormControl>
					</GridItem>
					<GridItem>
						<FormControl>
							<FormLabel>Total kg medios propios</FormLabel>
							<InputField isDisabled value={formatNumber(totalKgMediosPropios)} />
						</FormControl>
					</GridItem>
				</Grid>
				<Text textStyle="body2" color="black" fontWeight="700" mt="2rem">
					Otras aportaciones
				</Text>
				<Grid
					templateColumns="repeat(3, 1fr)"
					gap="2rem"
					alignItems="flex-end"
					mt="1rem"
					style={{ pageBreakInside: "avoid" }}
				>
					<GridItem>
						<FormControl>
							<FormLabel>kg en 12 meses</FormLabel>
							<InputField
								name="kilos_ano_otras_aportaciones"
								defaultValue={formatNumber(seccionDataActual?.kilos_ano_otras_aportaciones)}
								{...changeDetector("kilos_ano_otras_aportaciones")}
								isDisabled={isDisabled}
							/>
						</FormControl>
					</GridItem>
					<GridItem>
						<FormControl>
							<FormLabel>€ en 12 meses</FormLabel>
							<InputField
								name="euros_ano_otras_aportaciones"
								defaultValue={formatNumber(seccionDataActual?.euros_ano_otras_aportaciones)}
								{...changeDetector("euros_ano_otras_aportaciones")}
								isDisabled={isDisabled}
							/>
						</FormControl>
					</GridItem>
					<GridItem>
						<FormControl>
							<FormLabel>Total kg otras aportaciones</FormLabel>
							<InputField isDisabled value={formatNumber(totalKgOtrasAportaciones)} />
						</FormControl>
					</GridItem>
				</Grid>
				<Grid templateColumns="repeat(3, 1fr)" gap="2rem" alignItems="flex-end" style={{ breakInside: "avoid" }}>
					<GridItem colSpan={2} />
					<GridItem mt="3rem">
						<FormControl>
							<FormLabel>Total kg en 12 meses</FormLabel>
							<InputField isDisabled value={formatNumber(totalKgOtrosMedios)} />
						</FormControl>
					</GridItem>
				</Grid>
				<Divider margin="1.5rem 0" color="gray.200" />
				<Grid templateColumns="repeat(2, 1fr)" columnGap="2.25rem" rowGap="1.75rem" style={{ breakInside: "avoid" }}>
					<GridItem>
						{seccionDataActual?.datos_correctos_6 !== undefined && (
							<FormControl>
								<FormLabel>¿Los datos son correctos?</FormLabel>
								<Box width="fit-content">
									<CheckboxGroup
										disableNA
										name="datos_correctos_6"
										defaultValue={seccionDataActual?.datos_correctos_6}
										onChange={(name, value) => sendInputValue(name, value)}
										changed={changeDetector("datos_correctos_6")}
										isDisabled={isDisabled}
									/>
								</Box>
							</FormControl>
						)}
					</GridItem>
					<GridItem colSpan={3} mb=".5rem">
						<FormControl>
							<FormLabel>Comentarios</FormLabel>
							<TextAreaInput
								name="comentarios_6"
								defaultValue={seccionDataActual?.comentarios_6}
								{...changeDetector("comentarios_6")}
								isDisabled={isDisabled}
							/>
						</FormControl>
					</GridItem>
				</Grid>
			</Box>
			{!printView && (
				<FormControl display="flex" alignItems="center" justifyContent="space-between" mt="1rem">
					<SeccionSwitch
						hasNextSection
						seccionCompletada={seccionDataActual?.seccion_6_completada === 1}
						nextSection={handleNextSectionNavigation}
						onSeccionCompletadaChange={(flag) => {
							sendInputValue("seccion_6_completada", flag ? "1" : "0");
						}}
						isDisabled={selectorDisabled}
					/>
				</FormControl>
			)}
		</Box>
	);
};

export default Seccion06;
